import { APIProvider, useMapsLibrary } from "@vis.gl/react-google-maps";
import { cn } from "cn";
import {
  type ComponentProps,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { BwLoadingLine, BwLocationLine } from "@/components/ui/icons";
import { Input } from "@/components/ui/input/input";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import { env } from "@/env";

const DEFAULT_DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 3;

/**
 * Fields requested from Place Details. Kept minimal — Google bills per field
 * group, and everything here is in the Basic (cheapest) group.
 */
const PLACE_DETAIL_FIELDS = [
  "address_components",
  "formatted_address",
  "geometry.location",
  "place_id",
];

/**
 * A selected address, already split into the shape the app's place forms use.
 * Parts Google could not resolve come back as `""` / `null` so callers can fill
 * only what it actually knew.
 */
export type PlaceAddress = {
  formattedAddress: string;
  streetAddress: string;
  streetAddressLine2: string;
  city: string;
  /** Two-letter state code (`CA`), matching what the API and `StateField` store. */
  state: string;
  zip: string;
  latitude: number | null;
  longitude: number | null;
  placeId: string;
};

type AddressSuggestion = {
  placeId: string;
  primary: string;
  secondary: string;
  description: string;
};

/**
 * `id` / `aria-invalid` / `aria-describedby` are injected by `FieldWrapper`'s
 * `cloneElement` — they must reach the inner `Input` for the error outline and
 * the label association to work.
 */
export type PlacesAutocompleteProps = Pick<
  ComponentProps<typeof Input>,
  "id" | "aria-invalid" | "aria-describedby"
> & {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  onPlaceSelect: (place: PlaceAddress) => void;
  /** Fires when the input loses focus — picking a suggestion never steals focus, so it does not fire. */
  onBlur?: () => void;
  /** ISO country code the search is restricted to. `null` searches worldwide. */
  countryCode?: string | null;
  debounceMs?: number;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Clears the input after a suggestion is picked — for pure "search to fill" boxes. */
  clearOnSelect?: boolean;
};

/**
 * Google returns address parts as an untyped component list; this pulls out the
 * ones the place forms need. `route` and `administrative_area_level_1` use
 * `short_name` ("Main St", "CA") because that is what the API stores.
 */
function toPlaceAddress(place: google.maps.places.PlaceResult): PlaceAddress {
  const components = place.address_components ?? [];
  const longName = (type: string) =>
    components.find((component) => component.types.includes(type))?.long_name ??
    "";
  const shortName = (type: string) =>
    components.find((component) => component.types.includes(type))
      ?.short_name ?? "";

  return {
    formattedAddress: place.formatted_address ?? "",
    streetAddress: [longName("street_number"), shortName("route")]
      .filter(Boolean)
      .join(" "),
    streetAddressLine2: longName("subpremise"),
    city:
      longName("locality") ||
      longName("sublocality") ||
      longName("postal_town"),
    state: shortName("administrative_area_level_1"),
    zip: longName("postal_code"),
    latitude: place.geometry?.location?.lat() ?? null,
    longitude: place.geometry?.location?.lng() ?? null,
    placeId: place.place_id ?? "",
  };
}

/**
 * Address search box. Type an address, pick a suggestion, and the caller gets it
 * back already split into street / city / state / zip / coordinates.
 *
 * Self-contained: it mounts its own Google Maps provider so it can be dropped
 * anywhere without the caller wiring one up. Nesting inside an existing provider
 * is safe — the API loader is a module-level singleton and the parameters match.
 */
export function PlacesAutocomplete(props: PlacesAutocompleteProps) {
  return (
    <APIProvider
      apiKey={env.PUBLIC_GOOGLE_MAPS_API_KEY}
      solutionChannel="Birdwatch"
    >
      <PlacesAutocompleteInput {...props} />
    </APIProvider>
  );
}

function PlacesAutocompleteInput({
  value,
  defaultValue = "",
  onValueChange,
  onPlaceSelect,
  onBlur,
  countryCode = "us",
  debounceMs = DEFAULT_DEBOUNCE_MS,
  placeholder = "Start typing an address",
  disabled = false,
  className,
  clearOnSelect = false,
  ...ariaProps
}: PlacesAutocompleteProps) {
  const listboxId = useId();
  const placesLib = useMapsLibrary("places");

  const anchorRef = useRef<HTMLDivElement>(null);
  const requestIdRef = useRef(0);
  const debounceTimeoutRef = useRef<number | null>(null);
  const autocompleteServiceRef =
    useRef<google.maps.places.AutocompleteService | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(
    null,
  );
  const sessionTokenRef =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null);

  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue);
  const inputValue = isControlled ? value : internalValue;

  const [open, setOpen] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [isResolvingPlace, setIsResolvingPlace] = useState(false);
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => {
    if (!placesLib) return;
    autocompleteServiceRef.current ??= new placesLib.AutocompleteService();
    placesServiceRef.current ??= new placesLib.PlacesService(
      document.createElement("div"),
    );
  }, [placesLib]);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current !== null) {
        window.clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const setInputValue = useCallback(
    (nextValue: string) => {
      if (!isControlled) {
        setInternalValue(nextValue);
      }
      onValueChange?.(nextValue);
    },
    [isControlled, onValueChange],
  );

  const closeDropdown = useCallback(() => {
    setSuggestions([]);
    setOpen(false);
    setActiveIndex(-1);
  }, []);

  /** Invalidates any in-flight request and cancels a pending debounce. */
  const cancelPendingSearch = useCallback(() => {
    requestIdRef.current += 1;
    if (debounceTimeoutRef.current !== null) {
      window.clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
    setIsLoadingSuggestions(false);
  }, []);

  const fetchSuggestions = useCallback(
    async (query: string) => {
      const requestId = requestIdRef.current;
      const service = autocompleteServiceRef.current;
      if (!placesLib || !service) return;

      try {
        sessionTokenRef.current ??= new placesLib.AutocompleteSessionToken();

        const { predictions } = await service.getPlacePredictions({
          input: query,
          types: ["address"],
          sessionToken: sessionTokenRef.current,
          ...(countryCode
            ? { componentRestrictions: { country: countryCode } }
            : {}),
        });

        // A newer keystroke already superseded this request.
        if (requestId !== requestIdRef.current) return;

        const nextSuggestions = predictions.map((prediction) => ({
          placeId: prediction.place_id,
          primary: prediction.structured_formatting.main_text,
          secondary: prediction.structured_formatting.secondary_text ?? "",
          description: prediction.description,
        }));

        setSuggestions(nextSuggestions);
        setOpen(nextSuggestions.length > 0);
        setActiveIndex(nextSuggestions.length > 0 ? 0 : -1);
      } catch {
        // ZERO_RESULTS rejects too — an empty dropdown is the right outcome either way.
        if (requestId === requestIdRef.current) {
          closeDropdown();
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoadingSuggestions(false);
        }
      }
    },
    [closeDropdown, countryCode, placesLib],
  );

  const queueFetchSuggestions = useCallback(
    (query: string) => {
      cancelPendingSearch();

      if (query.trim().length < MIN_QUERY_LENGTH) {
        closeDropdown();
        return;
      }

      setIsLoadingSuggestions(true);
      debounceTimeoutRef.current = window.setTimeout(() => {
        debounceTimeoutRef.current = null;
        void fetchSuggestions(query.trim());
      }, debounceMs);
    },
    [cancelPendingSearch, closeDropdown, debounceMs, fetchSuggestions],
  );

  const fetchPlaceDetail = useCallback(
    (placeId: string) =>
      new Promise<google.maps.places.PlaceResult | null>((resolve) => {
        const service = placesServiceRef.current;
        if (!service) {
          resolve(null);
          return;
        }

        service.getDetails(
          {
            placeId,
            fields: PLACE_DETAIL_FIELDS,
            ...(sessionTokenRef.current
              ? { sessionToken: sessionTokenRef.current }
              : {}),
          },
          (place, status) => {
            resolve(status === "OK" && place ? place : null);
          },
        );
      }),
    [],
  );

  const handleSelectSuggestion = useCallback(
    async (suggestion: AddressSuggestion) => {
      // Drop any queued/in-flight prediction so a late response can't reopen the
      // dropdown on top of the resolved selection.
      cancelPendingSearch();
      closeDropdown();
      setIsResolvingPlace(true);

      try {
        const place = await fetchPlaceDetail(suggestion.placeId);
        if (!place) return;
        onPlaceSelect(toPlaceAddress(place));
      } finally {
        // The session ends with the details lookup — the next search starts a new one.
        sessionTokenRef.current = null;
        setIsResolvingPlace(false);
      }
    },
    [
      cancelPendingSearch,
      clearOnSelect,
      closeDropdown,
      fetchPlaceDetail,
      onPlaceSelect,
      setInputValue,
    ],
  );

  /**
   * The input lives outside the popover so it stays focused and typable, which
   * means cmdk cannot drive the list — arrow/enter handling is ours.
   */
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape" && open) {
      // Swallow it, or the surrounding dialog closes instead of the dropdown.
      event.preventDefault();
      event.stopPropagation();
      closeDropdown();
      return;
    }

    if (!open || suggestions.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((current) => (current + 1) % suggestions.length);
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex(
        (current) => (current - 1 + suggestions.length) % suggestions.length,
      );
      return;
    }

    if (event.key === "Enter") {
      // Always swallow Enter while the dropdown is open — otherwise it submits
      // the surrounding form instead of picking the highlighted address.
      event.preventDefault();
      const suggestion = suggestions[activeIndex];
      if (suggestion) {
        void handleSelectSuggestion(suggestion);
      }
    }
  };

  return (
    <Popover
      modal={false}
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) closeDropdown();
      }}
    >
      <PopoverAnchor ref={anchorRef} className={className}>
        <Input
          {...ariaProps}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          autoComplete="off"
          data-1p-ignore="true"
          value={inputValue}
          disabled={disabled}
          placeholder={placeholder}
          clearable={!isLoadingSuggestions && !isResolvingPlace}
          suffix={
            isLoadingSuggestions || isResolvingPlace ? (
              <BwLoadingLine
                size={16}
                className="text-text-dark-min animate-spin"
              />
            ) : null
          }
          onChange={(event) => {
            const nextValue = event.target.value;
            setInputValue(nextValue);
            queueFetchSuggestions(nextValue);
          }}
          onFocus={() => {
            if (suggestions.length > 0) setOpen(true);
          }}
          onBlur={onBlur}
          onClear={() => {
            cancelPendingSearch();
            closeDropdown();
          }}
          onKeyDown={handleKeyDown}
        />
      </PopoverAnchor>

      <PopoverContent
        id={listboxId}
        role="listbox"
        // No max-height: Google caps predictions at 5, so the list always fits.
        // A scroll container here would be dead weight — and unusable anyway,
        // since the dialog's scroll lock swallows wheel events over this layer.
        className="p-1"
        // Keep the caret in the input — the dropdown is a hint, not a focus stop.
        onOpenAutoFocus={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => {
          if (anchorRef.current?.contains(event.target as Node)) {
            event.preventDefault();
          }
        }}
      >
        {suggestions.map((suggestion, index) => (
          <button
            key={suggestion.placeId}
            type="button"
            role="option"
            aria-selected={index === activeIndex}
            onMouseDown={(event) => event.preventDefault()}
            onMouseEnter={() => setActiveIndex(index)}
            onClick={() => void handleSelectSuggestion(suggestion)}
            className={cn(
              /* sorted */
              "flex w-full items-center gap-2",
              "px-2 py-2",
              "text-left",
              "rounded-md",
              "cursor-pointer transition-colors",
              index === activeIndex && "bg-bg-dark-min",
            )}
          >
            <BwLocationLine
              size={20}
              className="text-text-dark-high mt-0.5 shrink-0"
            />
            <span className="block truncate text-sm text-text-dark-max">
              {suggestion.description}
            </span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
